import React, { useEffect, useRef } from 'react';
import './MapPreview.css';

/* ── Load Leaflet from CDN once (shared with LocationPickerModal) ── */
function loadLeaflet() {
  return new Promise((resolve) => {
    if (window.L) { resolve(); return; }
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id    = 'leaflet-css';
      link.rel   = 'stylesheet';
      link.href  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    const script   = document.createElement('script');
    script.src     = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload  = resolve;
    script.onerror = resolve;
    document.head.appendChild(script);
  });
}

/* ── Load + cache the Government-of-India-compliant national boundary
   (overlaid on top of the OSM base tiles, which show contested borders
   like J&K/Aksai Chin/Arunachal Pradesh per international convention
   rather than India's official boundary). ── */
let indiaBoundaryPromise = null;
function loadIndiaBoundary() {
  if (!indiaBoundaryPromise) {
    indiaBoundaryPromise = fetch('/india-boundary.geojson')
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
  }
  return indiaBoundaryPromise;
}

function addIndiaBoundary(L, map) {
  loadIndiaBoundary().then((geojson) => {
    if (!geojson) return;
    L.geoJSON(geojson, {
      style: {
        color: '#0B3D91',   // official-boundary line colour
        weight: 1.5,
        opacity: 0.85,
        fill: false,
      },
      interactive: false,   // don't intercept clicks/drag
    }).addTo(map);
  });
}

/* Custom green pin icon matching Naturiq brand */
function makeIcon(L) {
  return L.divIcon({
    className: '',
    html: `<svg width="28" height="38" viewBox="0 0 40 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 2C11.163 2 4 9.163 4 18c0 11.25 14.25 32.5 15.25 34a.9.9 0 0 0 1.5 0C21.75 50.5 36 29.25 36 18 36 9.163 28.837 2 20 2z"
        fill="#2D6A4F" stroke="#fff" stroke-width="2"/>
      <circle cx="20" cy="18" r="6" fill="#fff"/>
      <circle cx="20" cy="18" r="3" fill="#2D6A4F"/>
    </svg>`,
    iconSize:   [28, 38],
    iconAnchor: [14, 38],
  });
}

/* ── Small static map preview — Leaflet / OpenStreetMap version ── */
export default function MapPreview({ lat, lng }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (lat == null || lng == null) return;
    let cancelled = false;

    (async () => {
      await loadLeaflet();
      if (cancelled || !divRef.current) return;

      const L = window.L;
      if (!L) return;

      // Destroy previous instance if coords changed
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

      const map = L.map(divRef.current, {
        center:           [lat, lng],
        zoom:             15,
        zoomControl:      false,
        attributionControl: false,
        dragging:         false,
        scrollWheelZoom:  false,
        doubleClickZoom:  false,
        touchZoom:        false,
        keyboard:         false,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(map);

      addIndiaBoundary(L, map);

      L.marker([lat, lng], { icon: makeIcon(L) }).addTo(map);
      mapRef.current = map;
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [lat, lng]);

  return <div className="mpv-map" ref={divRef} />;
}